/**
 * Helper para converter URLs relativas da API em URLs absolutas
 *
 * O backend retorna URLs relativas como `/api/uploads/products/image.jpg`
 * que precisam ser convertidas para URLs absolutas usando a base URL da API
 * para funcionar corretamente no frontend (especialmente em desenvolvimento).
 */

/**
 * Obtém a base URL da API (mesma lógica do axios.ts)
 */
const getApiBaseURL = (): string => {
    const envApiURL = import.meta.env.VITE_API_URL;

    // Se não estiver definida, usar fallback relativo (funciona com proxy nginx)
    if (!envApiURL) {
        return '/api';
    }

    // Se for URL relativa (começa com /), usar como está (será relativa à origem atual)
    if (envApiURL.startsWith('/')) {
        return envApiURL;
    }

    // Se for URL absoluta, verificar se precisa ajustar o protocolo
    try {
        const url = new URL(envApiURL);
        const currentProtocol = window.location.protocol; // 'http:' ou 'https:'
        const isLocalhost = url.hostname === 'localhost' || url.hostname === '127.0.0.1';

        // NUNCA converter localhost para HTTPS (localhost raramente tem SSL em desenvolvimento)
        if (isLocalhost) {
            // Se for localhost e estiver configurado como HTTP, manter HTTP mesmo se site estiver em HTTPS
            if (url.protocol === 'http:') {
                return envApiURL; // Manter HTTP para localhost
            }
        }

        // Para URLs não-localhost: Se o site está em HTTPS mas a API está configurada para HTTP, ajustar para HTTPS
        if (!isLocalhost && currentProtocol === 'https:' && url.protocol === 'http:') {
            url.protocol = 'https:';
            return url.toString();
        }

        // Se o site está em HTTP mas a API está configurada para HTTPS, manter HTTPS (mais seguro)
        if (currentProtocol === 'http:' && url.protocol === 'https:') {
            // Manter HTTPS
        }

        return envApiURL;
    } catch (e) {
        // Se não for uma URL válida, usar como está
        return envApiURL;
    }
};

/**
 * Converte uma URL relativa da API em URL absoluta
 *
 * @param url - URL relativa (ex: `/api/uploads/products/image.jpg`) ou URL absoluta
 * @returns URL absoluta (ex: `http://localhost:3000/api/uploads/products/image.jpg`)
 *
 * @example
 * ```ts
 * getImageUrl('/api/uploads/products/image.jpg')
 * // Retorna: 'http://localhost:3000/api/uploads/products/image.jpg'
 *
 * getImageUrl('https://example.com/image.jpg')
 * // Retorna: 'https://example.com/image.jpg' (já é absoluta)
 * ```
 */
export const getImageUrl = (url: string | null | undefined): string => {
    // Se não houver URL, retornar string vazia
    if (!url) {
        return '';
    }

    // Se já for uma URL absoluta (começa com http:// ou https://), retornar como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    // Se for uma data URL (data:image/...), retornar como está
    if (url.startsWith('data:')) {
        return url;
    }

    // Se for uma URL relativa que começa com /api, converter para absoluta
    if (url.startsWith('/api/')) {
        const apiBaseURL = getApiBaseURL();

        // Se a base URL já é absoluta (ex: http://localhost:3000/api), concatenar
        if (apiBaseURL.startsWith('http://') || apiBaseURL.startsWith('https://')) {
            // A base URL já contém /api, então apenas remover /api da URL relativa e concatenar
            // Exemplo: url = '/api/uploads/products/image.jpg', apiBaseURL = 'http://localhost:3000/api'
            // Resultado: 'http://localhost:3000/api/uploads/products/image.jpg'
            const pathWithoutApi = url.replace(/^\/api/, '');
            return `${apiBaseURL}${pathWithoutApi}`;
        }

        // Se a base URL é relativa (ex: /api), retornar a URL relativa como está
        // (funcionará com proxy nginx em produção)
        return url;
    }

    // Para outras URLs relativas, retornar como está
    return url;
};
